import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { cn } from "@/lib/utils";
import { buttonChrome } from "@/components/ui/button";
import {
  MODAL_ACTIONS,
  MODAL_DESCRIPTION,
  MODAL_OVERLAY,
  MODAL_SURFACE,
  MODAL_TITLE,
} from "./modal-chrome";

const AlertDialog = AlertDialogPrimitive.Root;
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Overlay
  > | null>;
}) => (
  <AlertDialogPrimitive.Overlay
    // Named as the dialog's is: the overlay exposes no role.
    data-testid="alert-dialog-overlay"
    className={cn(MODAL_OVERLAY, className)}
    {...props}
    ref={ref}
  />
);
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

const AlertDialogContent = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Content
  > | null>;
}) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(MODAL_SURFACE, className)}
      {...props}
    />
  </AlertDialogPortal>
);
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(MODAL_ACTIONS, className)} {...props} />
);
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Title
  > | null>;
}) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn(MODAL_TITLE, className)}
    {...props}
  />
);
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Description
  > | null>;
}) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn(MODAL_DESCRIPTION, className)}
    {...props}
  />
);
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Action
  > | null>;
}) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(buttonChrome(), className)}
    {...props}
  />
);
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Cancel
  > | null>;
}) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(buttonChrome({ variant: "secondary" }), className)}
    {...props}
  />
);
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
